import { useState, useCallback, useMemo } from "react";

import { getUnitEmployees, getEmployeesOptions } from "@/api/employees";
import {
  getEmployeeUnitsOptions,
  getUnitsOptions,
  getItem as getUnit,
  getUnitsTree,
} from "@/api/units";

import routes from "@/routes";

const INITIAL_FILTERS = {
  unit_uuid: null,
  units_uuids: [],
};

const getDefaultFilters = (unit_uuid) => ({
  ...INITIAL_FILTERS,
  ...(unit_uuid && { unit_uuid }),
});

const getInitialFilters = (locationSearch, unit_uuid) => {
  const params = new URLSearchParams(locationSearch);
  const filters = getDefaultFilters(unit_uuid);

  for (const key in filters) {
    if (params.has(key)) {
      filters[key] = params.get(key);
    }
  }

  return filters;
};

const useCatalogFilters = ({
  locationSearch,
  unit_uuid,
  isAdmins,
  navigate,
  setUnitData,
  message,
}) => {
  const [filters, setFilters] = useState(() =>
    getInitialFilters(locationSearch, unit_uuid),
  );
  const [fileType, setFileType] = useState("all");
  const [authorUuid, setAuthorUuid] = useState(null);
  const [search, setSearch] = useState("");

  // Списки для фильтров
  const [units, setUnits] = useState([]);
  const [unitsTree, setUnitsTree] = useState([]);
  const [authors, setAuthors] = useState([]);

  const [optionsPending, setOptionsPending] = useState(true);

  // Окно с фильтрами
  const [isFiltersModalShow, setIsFiltersModalShow] = useState(false);
  const [tempValueFilters, setTempValueFilters] = useState(null);

  const isAnyFilterApplied = Boolean(
    filters.unit_uuid ||
    (Array.isArray(filters.units_uuids) && filters.units_uuids.length) ||
    fileType !== "all" ||
    authorUuid ||
    search.trim(),
  );

  const fetchOptions = async () => {
    setOptionsPending(true);

    try {
      const [{ units }, unit, tree] = await Promise.all([
        isAdmins ? await getUnitsOptions() : await getEmployeeUnitsOptions(),
        unit_uuid && getUnit(unit_uuid),
        unit_uuid
          ? getUnitsTree({ unit_uuid: unit_uuid, from_unit: true })
          : getUnitsTree({ from_unit: true }),
      ]);

      setUnits(units);
      setUnitData(unit);
      setUnitsTree(tree);

      fetchUserList();

      // Если фильтр по рабочей группе, но ее нет в списке, сбросить фильтр
      if (
        filters.unit_uuid &&
        !units.some((unit) => unit.uuid === filters.unit_uuid)
      ) {
        setFilters({
          ...filters,
          unit_uuid: null,
        });
        fetchUserList();
      }

      // Если на общей странице каталога без фильтров, но они подтянулись с предыдущего места, то нужно их почистить
      if (filters.unit_uuid && !unit_uuid) {
        const params = new URLSearchParams(locationSearch);
        if (!params.has("unit_uuid")) {
          setFilters({
            ...filters,
            unit_uuid: null,
          });
          fetchUserList();
        }
      }

      // Открыли папку конкретной РГ
      if (unit_uuid && filters.unit_uuid !== unit_uuid) {
        setFilters({
          ...filters,
          unit_uuid,
        });
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setOptionsPending(false);
    }
  };

  // Возвращает плоский список РГ
  const getFlatUnits = useMemo(() => {
    const flat = [];

    function traverse(nodes) {
      nodes.forEach((node) => {
        flat.push(node);
        if (Array.isArray(node.children) && node.children.length > 0) {
          traverse(node.children);
        }
      });
    }

    if (!unitsTree) return [];

    traverse(unitsTree);
    return flat;
  }, [unitsTree]);

  const fetchUserList = useCallback(
    async (unit_uuid) => {
      try {
        let list = [];
        if (isAdmins && !unit_uuid) {
          const { employees } = await getEmployeesOptions({
            is_dismissed: false,
          });
          list = employees.map(({ employee }) => employee);
        } else {
          const { members } = await getUnitEmployees({
            ...(unit_uuid && {
              unit_uuid: unit_uuid,
            }),
          });
          list = members;
        }

        setAuthors(
          list.map((item) => {
            const { lastname, firstname, patronymic } = item;
            return {
              ...item,
              label: `${lastname || ""} ${firstname || ""} ${patronymic || ""}`,
            };
          }),
        );
      } catch (error) {
        console.log(error);
      }
    },
    [isAdmins],
  );

  const handleSelectUnit = (val) => {
    setFilters((prev) => ({
      ...prev,
      unit_uuid: val || null,
    }));

    navigate(isAdmins ? routes.administration.folder() : routes.folder(), {
      replace: true,
    });
  };

  const handleSelectAuthor = (val) => {
    setAuthorUuid(val);
  };

  // Обработчик для открытия модалки с фильтрами
  const openFiltersModal = () => {
    setTempValueFilters({
      unit_uuid: filters.unit_uuid,
      units_uuids: filters.units_uuids,
      file_type: fileType,
      author_uuid: authorUuid,
    });
    setIsFiltersModalShow(true);
  };

  const clearFilters = () => {
    setFilters(getDefaultFilters(unit_uuid));
    setFileType("all");
    setAuthorUuid(null);
    setSearch("");

    navigate(
      unit_uuid
        ? routes.unitFolder(unit_uuid)
        : isAdmins
          ? routes.administration.folder()
          : routes.folder(),
      { replace: true },
    );

    fetchUserList();
  };

  // Применение выбранных фильтров из модалки
  const applyFilters = useCallback(() => {
    setFilters({
      unit_uuid: tempValueFilters.unit_uuid,
      units_uuids: tempValueFilters.units_uuids,
    });
    setFileType(tempValueFilters.file_type);
    setAuthorUuid(tempValueFilters.author_uuid);
    setTempValueFilters(null);
    setIsFiltersModalShow(false);
  }, [tempValueFilters]);
  // Обработчик для смены временных фильтров в модалке
  const handleTempFilterChange = useCallback(
    (name, val) => {
      setTempValueFilters((prev) => {
        const additionalFilters = {};

        if (name === "units_uuids") {
          additionalFilters.file_type = "all";
        }

        if (name === "file_type") {
          additionalFilters.units_uuids =
            val === "all"
              ? []
              : val === "root"
                ? getFlatUnits.filter(({ readOnly }) => !readOnly)
                : getFlatUnits.filter(({ readOnly }) => readOnly);
        }

        return {
          ...prev,
          [name]: val || null,
          ...additionalFilters,
        };
      });

    },
    [getFlatUnits],
  );

  return {
    filters,
    setFilters,
    fileType,
    setFileType,
    authorUuid,
    setAuthorUuid,
    search,
    setSearch,
    isAnyFilterApplied,
    handleSelectUnit,
    handleSelectAuthor,
    openFiltersModal,
    clearFilters,
    isFiltersModalShow,
    setIsFiltersModalShow,
    tempValueFilters,
    setTempValueFilters,
    applyFilters,
    handleTempFilterChange,
    fetchOptions,
    units,
    unitsTree,
    authors,
    getFlatUnits,
    optionsPending,
  };
};

export default useCatalogFilters;
